// @ts-nocheck
import React, { useState } from 'react';
import { Send, Paperclip, Eye, EyeOff } from 'lucide-react';

const ReplySection = ({ ticket, onReplySubmit, userRole, userId }) => {
  const [replyText, setReplyText] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isInternalNote, setIsInternalNote] = useState(false);

  const formatRelativeTime = (date) => {
    const now = new Date();
    const ticketDate = new Date(date);
    const diffInHours = Math.floor((now - ticketDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than an hour ago';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) return;

    setIsSubmittingReply(true);

    try {
      const newReply = {
        id: `reply-${Date.now()}`,
        ticketId: ticket.id,
        authorId: userId,
        authorName: userRole === 'customer' ? ticket.customerName : 'Support Agent',
        authorType: userRole,
        content: replyText.trim(),
        createdAt: new Date(),
        isInternal: isInternalNote && userRole !== 'customer',
        attachments: []
      };

      await onReplySubmit(newReply);
      setReplyText('');
      setIsInternalNote(false);
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Filter out internal notes for customers
  const visibleReplies = (ticket.replies || []).filter(reply => 
    userRole !== 'customer' || !reply.isInternal
  );

  // Don't show reply section for closed tickets
  if (ticket.status === 'closed') {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Existing Replies */}
      {visibleReplies.length > 0 && (
        <div className="p-6 border-b border-line">
          <h3 className="text-sm font-medium text-content mb-4">
            Conversation ({visibleReplies.length})
          </h3>
          <div className="space-y-4">
            {visibleReplies.map((reply) => (
              <div key={reply.id} className="flex gap-3">
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    reply.authorType === 'customer' 
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                      : reply.isInternal
                      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  }`}>
                    {reply.authorName.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-content">
                      {reply.authorName}
                    </span>
                    <span className="text-xs text-content-muted">
                      {formatRelativeTime(reply.createdAt)}
                    </span>
                    {reply.authorType === 'agent' && !reply.isInternal && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        Support Team
                      </span>
                    )}
                    {reply.isInternal && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Internal Note
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {reply.content}
                  </div>
                  {reply.attachments && reply.attachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {reply.attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center gap-2 text-xs text-content-muted">
                          <Paperclip className="w-3 h-3" />
                          <span>{attachment.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reply Form */}
      {(ticket.status === 'open' || ticket.status === 'in_progress' || ticket.status === 'resolved') && (
        <div className="p-6 border-t border-line bg-gray-50 dark:bg-dark-700">
          <form onSubmit={handleReplySubmit}>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="reply" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {userRole === 'customer' ? 'Add a Reply' : 'Add Response or Note'}
                </label>
                
                {/* Internal Note Toggle for agents/managers */}
                {userRole !== 'customer' && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsInternalNote(!isInternalNote)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        isInternalNote
                          ? 'bg-orange-100 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/40'
                          : 'bg-gray-100 dark:bg-dark-700 text-content-secondary hover:bg-gray-200'
                      }`}
                    >
                      {isInternalNote ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      {isInternalNote ? 'Internal Note' : 'Public Reply'}
                    </button>
                  </div>
                )}
              </div>
              
              <textarea
                id="reply"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={4}
                placeholder={
                  isInternalNote 
                    ? "Add an internal note (not visible to customer)..."
                    : "Type your message here..."
                }
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-xs text-content-muted">
                  {isInternalNote 
                    ? 'This note will only be visible to support team members'
                    : userRole === 'customer' 
                      ? 'Your reply will be visible to the support team'
                      : 'Your reply will be visible to the customer'
                  }
                </div>
                
                {/* Future: Attachment button */}
                <button
                  type="button"
                  className="text-content-subtle hover:text-content-secondary transition-colors"
                  title="Attach file (coming soon)"
                  disabled
                >
                  <Paperclip className="w-4 h-4" />
                </button>
              </div>
              
              <button
                type="submit"
                disabled={!replyText.trim() || isSubmittingReply}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmittingReply ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {isInternalNote ? 'Add Note' : 'Send Reply'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ReplySection;